<script lang="ts">
	import { format } from 'src/services/i18n';
	import type { Device } from 'src/types/Device';
	import { app, devices_status } from 'src/stores';
	import { AlertV2, IconButton, Input, Text } from '@Shelly/shelly-design-system';
	import { get_ip, get_local_ip, real_id } from 'src/helpers/interface_helpers';
	import { copy } from 'src/helpers/copy_text';
	import { getCameraConfig } from '../../../helpers/camera-settings-helpers';

	export let device: Device;

	/** Fixed RTSP port of the camera firmware. */
	const RTSP_PORT = 554;

	const realId = real_id(device.id);

	const streamLabel = (key: string): string =>
		key === '0'
			? $format('camera_stream_main', { default: 'Main stream' })
			: $format('camera_substream_n', { values: { n: key }, default: `Substream #${key}` });

	$: ip = get_local_ip(device.id) || (!$app && get_ip($devices_status[realId])) || '';

	$: streamKeys = Object.keys(getCameraConfig(device)?.streams ?? { 0: {} }).sort(
		(a, b) => Number(a) - Number(b),
	);

	$: addresses = ip
		? streamKeys.map((key) => ({
				key,
				label: streamLabel(key),
				url: `rtsp://${ip}:${RTSP_PORT}/stream/${key}`,
			}))
		: [];

	const onCopy = (url: string) =>
		copy(url, $format('camera_rtsp_address_copied', { default: 'RTSP address copied.' }));
</script>

{#if addresses.length}
	{#each addresses as { key, label, url } (key)}
		<div class="width-full mt-s" style="min-width: 0;">
			<Text styleType="body-extra-small" class="text-color-muted mb-xs d-block">{label}</Text>
			<div class="d-flex align-items-end gap-xs">
				<Input
					id="camera-rtsp-address-{key}"
					class="width-full"
					size="l"
					readonly
					value={url}
					aria-label={label}
				/>
				<IconButton
					icon="fas fa-copy"
					on:click={() => onCopy(url)}
					aria-label={$format('button_copy_to_clipboard', { default: 'Copy to clipboard' })}
				/>
			</div>
		</div>
	{/each}
{:else}
	<AlertV2
		class="mt-s"
		customIcon="fas fa-circle-info text-primary"
		message={$format('camera_rtsp_no_address', {
			default:
				'The stream address appears here once you are on the same local network as the camera.',
		})}
	/>
{/if}
